import { useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { CSV_MAX_ROWS } from '../useCsvModal'
import { Icon } from '@/Atomic/Atoms/Icon'
import { InfoBox } from '@/Atomic/Molecules/InfoBox'

interface SelectFileStepProps {
  file: File | null
  onFileSelect: (file: File) => void
  rowLimitExceeded: boolean
  isMatchingAI: boolean
  /** Dev-only diagnostic control, rendered as-is; omitted in production. */
  diagnostic?: React.ReactNode
}

export const SelectFileStep: React.FC<SelectFileStepProps> = ({
  file,
  onFileSelect,
  rowLimitExceeded,
  isMatchingAI,
  diagnostic,
}) => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const accept = (incoming: File | null | undefined) => {
    // Ignore any new selection while an AI match is in flight: a second file
    // would start a concurrent handleFileSelect whose older AI result could
    // resolve last and overwrite the newer file's rows/mappings.
    if (isMatchingAI) return
    if (!incoming?.name.endsWith('.csv')) return
    onFileSelect(incoming)
  }

  const openPicker = () => {
    if (isMatchingAI) return
    inputRef.current?.click()
  }

  return (
    <>
      <div
        className={classNames('csv-dropzone', {
          'is-dragging': isDragging,
          'has-file': Boolean(file),
          'has-error': rowLimitExceeded,
          'is-disabled': isMatchingAI,
        })}
        onDragOver={(e) => {
          e.preventDefault()
          if (!isMatchingAI) setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setIsDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          accept(e.dataTransfer.files[0])
        }}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        aria-disabled={isMatchingAI}
        onKeyDown={(e) => e.key === 'Enter' && openPicker()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="csv-dropzone-input"
          disabled={isMatchingAI}
          onChange={(e) => accept(e.target.files?.[0])}
        />
        <Icon
          icon={file ? 'file-check' : 'upload'}
          size="lg"
          className="csv-dropzone-icon"
        />
        {file ? (
          <span className="csv-dropzone-filename wizard-modal-label">
            {file.name}
          </span>
        ) : (
          <>
            <span className="csv-dropzone-label wizard-modal-label">
              {t('components.csv.dropLabel')}
            </span>
            <span className="csv-dropzone-sublabel wizard-modal-meta">
              {t('components.csv.orClick')}
            </span>
          </>
        )}
      </div>
      {isMatchingAI && (
        <div className="csv-matching-indicator">
          <Icon icon="bubble-chat-ai" size="sm" className="csv-matching-icon" />
          <span className="wizard-modal-meta">
            {t('components.csv.matching.analysing')}
          </span>
          <span className="csv-matching-dots" aria-hidden="true">
            <span className="csv-matching-dot" />
            <span className="csv-matching-dot" />
            <span className="csv-matching-dot" />
          </span>
        </div>
      )}
      {rowLimitExceeded && (
        <div className="csv-dropzone-error">
          <InfoBox type="danger">
            {t('components.csv.rowLimitExceeded', { max: CSV_MAX_ROWS })}
          </InfoBox>
        </div>
      )}
      {diagnostic}
    </>
  )
}
